/* Blocks checkout until wholesale cartons are complete.
   Lines added from the wholesale order page carry hidden _carton_group, _carton_size and _step
   properties (see wholesale-collection.js). The cart is checked whenever the theme re-renders it
   and again whenever someone clicks a checkout button, including the one inside the Upcart
   slide-out cart, which keeps its own copy of the cart.
   Also provides the friendly "carton isn't full" pop-up, which the order page uses too
   (window.WholesaleCartons.showPopup, see wholesale-order-ui.js). */
(() => {
  const cartUrl = (window.routes && window.routes.cartUrl) || '/cart';
  const orderUrl = '/collections/wholesale';
  const CHECKOUT = 'button[name="checkout"], input[name="checkout"]';

  let problems = { groups: [], notes: [] };
  let bypassUntil = 0;

  const style = document.createElement('style');
  style.textContent = `
    .wholesale-cart-warning { margin: 0 0 12px; padding: 12px 14px; border-radius: 8px; background: var(--error-background, #fdecea); color: var(--error-color, #c0392b); font-size: 14px; line-height: 1.45; text-align: left; }
    .wholesale-cart-warning__title { margin: 0 0 4px; font-weight: var(--text-font-bolder-weight, 600); }
    .wholesale-cart-warning ul { margin: 0 0 6px; padding-left: 18px; list-style: disc; }
    .wholesale-cart-warning a { color: inherit; font-weight: var(--text-font-bolder-weight, 600); text-decoration: underline; }
    .is-wholesale-checkout-blocked [name="checkout"] { opacity: .4; cursor: not-allowed; }
    .wholesale-popup { width: min(420px, calc(100vw - 32px)); max-width: none; padding: 0; border: 0; border-radius: 18px; background: var(--background, #fff); color: var(--text-color, #333); box-shadow: 0 24px 60px rgba(0, 0, 0, .3); }
    .wholesale-popup[open] { animation: wholesale-pop .25s cubic-bezier(.2, .9, .3, 1.3); }
    .wholesale-popup::backdrop { background: rgba(0, 0, 0, .45); }
    .wholesale-popup__inner { position: relative; padding: 28px 24px 20px; text-align: center; }
    .wholesale-popup__icon { margin: 0 0 6px; font-size: 44px; line-height: 1; }
    .wholesale-popup__title { margin: 0 0 8px; color: var(--heading-color, inherit); font-family: var(--heading-font-family, inherit); font-size: 21px; line-height: 1.3; }
    .wholesale-popup__body { margin: 0 0 20px; font-size: 15px; line-height: 1.55; }
    .wholesale-popup__body p { margin: 0; }
    .wholesale-popup__body ul { margin: 0 0 12px; padding: 0; list-style: none; text-align: left; }
    .wholesale-popup__body li { margin-top: 6px; padding: 8px 12px; border-radius: 8px; background: rgba(var(--text-color-rgb, 0, 0, 0), .05); }
    .wholesale-popup__actions { display: flex; flex-direction: column; align-items: center; gap: 6px; }
    .wholesale-popup__actions .button { width: 100%; }
    .wholesale-popup__secondary { padding: 6px; border: 0; background: none; color: inherit; font: inherit; font-size: 14px; text-decoration: underline; cursor: pointer; }
    .wholesale-popup__close { position: absolute; top: 8px; right: 8px; width: 36px; height: 36px; padding: 0; border: 0; border-radius: 50%; background: none; color: inherit; font-size: 24px; line-height: 1; cursor: pointer; }
    .wholesale-popup__close:hover { background: rgba(var(--text-color-rgb, 0, 0, 0), .06); }
    @keyframes wholesale-pop { from { opacity: 0; transform: scale(.9) translateY(8px); } }
  `;
  document.head.append(style);

  /* "Carton isn't full" pop-up */

  const pick = (options) => options[Math.floor(Math.random() * options.length)];

  const popupButton = (tag, className, label, onClick) => {
    const button = document.createElement(tag);
    button.className = className;
    button.textContent = label;
    if (tag === 'button') button.type = 'button';
    button.addEventListener('click', onClick);
    return button;
  };

  // groups: [{ name, missing, size }]; notes: other problems as text.
  // primary/secondary: { label, href } or { label, onClick }. The pop-up closes before either runs.
  const showPopup = ({ groups = [], notes = [], primary, secondary }) => {
    document.querySelectorAll('.wholesale-popup').forEach((element) => element.remove());

    const dialog = document.createElement('dialog');
    const inner = document.createElement('div');
    const icon = document.createElement('p');
    const title = document.createElement('h2');
    const body = document.createElement('div');
    const actions = document.createElement('div');
    const message = document.createElement('p');

    dialog.className = 'wholesale-popup';
    dialog.setAttribute('aria-labelledby', 'wholesale-popup-title');
    inner.className = 'wholesale-popup__inner';
    icon.className = 'wholesale-popup__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '🍬';
    title.className = 'wholesale-popup__title';
    title.id = 'wholesale-popup-title';
    body.className = 'wholesale-popup__body';
    actions.className = 'wholesale-popup__actions';

    if (groups.length === 1 && notes.length === 0) {
      const { name, missing, size } = groups[0];
      title.textContent = `Oops! Your ${name} carton isn’t full`;
      message.textContent = pick([
        `There’s room for ${missing} more in there, and our sweets hate travelling with empty seats.`,
        `Just ${missing} more and it’s a full carton of ${size}. Mix and match any flavours you like!`,
        `Your carton’s looking a little lonely. Pop in ${missing} more to fill it up.`
      ]);
      body.append(message);
    } else {
      const list = document.createElement('ul');
      title.textContent = groups.length > 1 ? 'Oops! A few cartons aren’t full yet' : 'Oops! Your order needs a little tweak';
      groups.forEach(({ name, missing, size }) => {
        const item = document.createElement('li');
        item.textContent = `${name}: add ${missing} more to fill a carton of ${size}`;
        list.append(item);
      });
      notes.forEach((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        list.append(item);
      });
      message.textContent = 'Our sweets hate travelling with empty seats, so top them up and you’re good to go!';
      body.append(list, message);
    }

    const run = (action) => () => {
      dialog.close();
      if (action.onClick) action.onClick();
    };

    if (primary) {
      const link = popupButton(primary.href ? 'a' : 'button', 'button button--primary', primary.label, run(primary));
      if (primary.href) link.href = primary.href;
      actions.append(link);
    }

    if (secondary) {
      actions.append(popupButton('button', 'wholesale-popup__secondary', secondary.label, run(secondary)));
    }

    const close = popupButton('button', 'wholesale-popup__close', '×', () => dialog.close());
    close.setAttribute('aria-label', 'Close');

    inner.append(close, icon, title, body, actions);
    dialog.append(inner);
    document.body.append(dialog);

    // Clicking the dimmed backdrop closes the pop-up.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => dialog.remove());

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.remove();
      window.alert(`${title.textContent}\n\n${body.textContent}`);
    }
  };

  window.WholesaleCartons = { showPopup };

  /* Cart check */

  const findProblems = (cart) => {
    const totals = {};
    const found = { groups: [], notes: [] };

    (cart.items || []).forEach((item) => {
      const props = item.properties || {};
      const size = Number(props._carton_size) || 0;

      if (size === 0) {
        return;
      }

      const step = Number(props._step) || 1;
      const name = props._carton_group || 'Wholesale';

      if (item.quantity % step !== 0) {
        found.notes.push(`${item.product_title}: order in multiples of ${step} (currently ${item.quantity}).`);
      }

      totals[name] = totals[name] || { size, units: 0 };
      totals[name].units += item.quantity;
    });

    Object.keys(totals).forEach((name) => {
      const { size, units } = totals[name];
      const remainder = units % size;

      if (remainder > 0) {
        found.groups.push({
          name,
          size,
          missing: size - remainder,
          text: `${name}: ${units} in your cart. Cartons hold ${size}, so add ${size - remainder} more or remove ${remainder}.`
        });
      }
    });

    return found;
  };

  const hasProblems = () => problems.groups.length + problems.notes.length > 0;

  const buildMessage = () => {
    const box = document.createElement('div');
    const title = document.createElement('p');
    const list = document.createElement('ul');
    const link = document.createElement('a');

    box.className = 'wholesale-cart-warning';
    box.setAttribute('role', 'alert');
    title.className = 'wholesale-cart-warning__title';
    title.textContent = 'Cartons incomplete: checkout is unavailable';

    problems.groups.map((group) => group.text).concat(problems.notes).forEach((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      list.append(item);
    });

    link.href = orderUrl;
    link.textContent = 'Adjust your wholesale order';
    box.append(title, list, link);

    return box;
  };

  const render = () => {
    document.querySelectorAll('.wholesale-cart-warning').forEach((element) => element.remove());
    document.documentElement.classList.toggle('is-wholesale-checkout-blocked', hasProblems());

    if (hasProblems()) {
      document.querySelectorAll(CHECKOUT).forEach((button) => {
        button.insertAdjacentElement('beforebegin', buildMessage());
      });
    }
  };

  const check = async () => {
    try {
      const response = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      problems = findProblems(await response.json());
    } catch (error) {
      problems = { groups: [], notes: [] };
    }

    render();
  };

  const checkoutControl = (target) => {
    const control = target && target.closest && target.closest('button, a, input');

    if (!control) {
      return null;
    }

    if (control.matches(CHECKOUT)) {
      return control;
    }

    if (control.closest('#upCart')) {
      const href = control.getAttribute('href') || '';

      if (/\/checkout/.test(href) || /check\s*out/i.test(control.textContent || '')) {
        return control;
      }
    }

    return null;
  };

  // Capture phase, so this runs before the theme, Upcart or any other app handles the click.
  // The cart is re-checked first; if it passes, the original click is replayed.
  const block = async (event) => {
    if (Date.now() < bypassUntil) {
      return;
    }

    const control = checkoutControl(event.type === 'submit' ? event.submitter : event.target);

    if (!control) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    await check();

    if (!hasProblems()) {
      bypassUntil = Date.now() + 1500;
      control.click();
      return;
    }

    showPopup({
      groups: problems.groups,
      notes: problems.notes,
      primary: { label: 'Fill it up', href: orderUrl },
      secondary: { label: 'Close' }
    });
  };

  window.addEventListener('click', block, true);
  window.addEventListener('submit', block, true);

  // The theme fires this after re-rendering the cart page or mini cart. It doesn't bubble
  // from the mini cart, so listen in the capture phase.
  document.addEventListener('cart:rerendered', check, true);

  if (window.theme && window.theme.cartCount > 0) {
    check();
  }
})();